<template>
    <Card>
        <div v-if="canView" class="inventory-counts">
            <div class="inventory-counts__header">
                <div>
                    <h2>Inventory counts</h2>
                    <p>Assigned counts stay scoped to their location and assignees.</p>
                </div>
                <router-link v-if="canCreate" to="/inventory-counts/new">
                    <Button type="button" :noMargin="true">Create inventory count</Button>
                </router-link>
            </div>
            <form class="inventory-counts__filters" @submit.prevent="applyFilters">
                <label>Location <select v-model="warehouseId" class="form-control"><option value="">All locations</option><option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.title || warehouse.name }}</option></select></label>
                <label>Status <select v-model="status" class="form-control"><option value="">All statuses</option><option value="DRAFT">Draft</option><option value="IN_PROGRESS">In progress</option><option value="PENDING_REVIEW">Pending review</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option></select></label>
                <Button type="submit" :noMargin="true" :disabled="loading">{{ loading ? "Loading..." : "Refresh" }}</Button>
            </form>
            <UiState v-if="loading" type="loading" title="Loading inventory counts" compact />
            <UiState v-else-if="error" type="error" :message="error" actionLabel="Try again" compact @action="fetchCounts" />
            <UiState v-else-if="!counts.length" type="empty" title="No inventory counts" compact />
            <CustomTable v-else>
                <template #head><tr><th>Name</th><th>Date</th><th>Location</th><th>Mode</th><th>Assigned</th><th>Status</th><th>Actions</th></tr></template>
                <template #body>
                    <tr v-for="count in counts" :key="count.id">
                        <td>{{ count.name }}</td><td>{{ countDate(count.count_date) }}</td><td>{{ locationName(count) }}</td>
                        <td>{{ modeLabel(count) }}<span v-if="count.blind_count"> · Blind</span></td>
                        <td>{{ assignedLabel(count) }}</td><td>{{ statusLabel(count.status) }}</td>
                        <td><router-link :to="`/inventory-counts/${count.id}`"><Button type="button" :noMargin="true">View</Button></router-link></td>
                    </tr>
                </template>
            </CustomTable>
            <Paginator v-if="totalPages > 1" :totalPages="totalPages" :page="page" @change="changePage" />
        </div>
        <UiState v-else type="empty" title="No permission" compact />
    </Card>
</template>

<script>
import { mapState, mapActions } from "vuex"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import UiState from "../../components/UI/UiState.vue"
import CustomTable from "../../components/layout/CustomTable.vue"
import Paginator from "../../components/other/Paginator.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"
import { inventoryCountCalendarDate } from "../../utils/inventory-count-date"
import { inventoryCountLocationName } from "../../utils/inventory-count-location"

export default {
    name: "InventoryCountsPage",
    components: { Card, Button, UiState, CustomTable, Paginator },
    data: () => ({ counts: [], page: 1, limit: 20, totalPages: 0, loading: false, error: "", warehouseId: "", status: "", appliedWarehouseId: "", appliedStatus: "", countsRequest: 0, PERMISSION_CODES }),
    computed: {
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["warehouses"]),
        canView() { return this.has(PERMISSION_CODES.CMS_VIEW_INVENTORY_COUNT, PERMISSION_CODES.CMS_VIEW_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_INVENTORY_COUNT, PERMISSION_CODES.CMS_CREATE_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER) },
        canCreate() { return this.has(PERMISSION_CODES.CMS_CREATE_INVENTORY_COUNT, PERMISSION_CODES.CMS_CREATE_RECONCILIATION, PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER) },
    },
    mounted() { this.setData({ pageTitle: "Inventory Counts" }); if (this.canView) this.fetchCounts() },
    methods: {
        ...mapActions("misc", ["setData"]),
        has(...permissions) { return Boolean(this.user?.isAdmin || permissions.some((permission) => checkHasPermission({ isAdmin: false, permissionId: permission, userPermissionIds: this.user?.permissionIds || [] }))) },
        applyFilters() {
            this.page = 1
            this.appliedWarehouseId = this.warehouseId
            this.appliedStatus = this.status
            return this.fetchCounts()
        },
        async fetchCounts() {
            const request = ++this.countsRequest
            this.loading = true; this.error = ""
            try {
                const query = new URLSearchParams({ page: String(this.page), limit: String(this.limit), warehouse_id: this.appliedWarehouseId, status: this.appliedStatus })
                const response = await http.get(`/inventory-counts?${query.toString()}`)
                if (request !== this.countsRequest) return
                this.counts = Array.isArray(response.data?.result) ? response.data.result : []
                this.totalPages = Number(response.data?.totalPages || 0)
            } catch (error) {
                if (request === this.countsRequest) this.error = error?.response?.data?.message || "Could not load inventory counts."
            } finally {
                if (request === this.countsRequest) this.loading = false
            }
        },
        changePage(page) { this.page = page; return this.fetchCounts() },
        countDate(value) { return inventoryCountCalendarDate(value) },
        locationName(count) { return inventoryCountLocationName(count, this.warehouses) },
        modeLabel(count) { return String(count.count_type || "LOT_PACKAGE").toUpperCase() === "BY_PRODUCT" ? "By product" : "By lot / package" },
        assignedLabel(count) { return Number(count.assignee_count || (Array.isArray(count.assigned_user_ids) ? count.assigned_user_ids.length : 0)) ? `${count.assignee_count || count.assigned_user_ids.length} user(s)` : "All authorized users" },
        statusLabel(status) { return String(status || "DRAFT").replace(/_/g, " ").toLowerCase().replace(/(^| )\w/g, (letter) => letter.toUpperCase()) },
    },
}
</script>

<style lang="scss" scoped>
.inventory-counts__header { display: flex; justify-content: space-between; gap: 16px; align-items: center; margin-bottom: 18px; }
.inventory-counts__header h2, .inventory-counts__header p { margin: 0; }
.inventory-counts__header p { margin-top: 4px; opacity: .72; }
.inventory-counts__filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; margin-bottom: 18px; }
.inventory-counts__filters label { min-width: 180px; display: grid; gap: 5px; font-weight: 600; }
</style>
